import React from "react";
import { Modal, Pressable, ScrollView, Text, View } from "react-native";

import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import {
  PhoneNumberText,
  PrimaryButton,
  SearchInput,
} from "../../../../common/native";
import type {
  TCommunicationsCall,
  TCommunicationsCallId,
} from "../../../../../services/api-communications";
import type { TMessageConversationListItem, TPatientOption } from "../types";
import { styles } from "../HomeConversations.styles";
import { normalizePatientPhoneForCall } from "../utils/phones";
import { formatPhoneNumberDisplay } from "../../../../../utils/phoneNumber";

type AssignPatientModalProps = {
  visible: boolean;
  submitting: boolean;
  targetCall: TCommunicationsCall | null;
  targetCallId: TCommunicationsCallId | null;
  targetConversation: TMessageConversationListItem | null;
  patientSearchTerm: string;
  onPatientSearchTermChange: (value: string) => void;
  patientSearchLoading: boolean;
  patientSearchError: string | null;
  patientOptions: TPatientOption[];
  selectedPatientUuid: string;
  onSelectPatientUuid: (value: string) => void;
  selectedPatient: TPatientOption | null;
  onClose: () => void;
  onAssign: () => void;
};

const AssignPatientModal: React.FC<AssignPatientModalProps> = ({
  visible,
  submitting,
  targetCall,
  targetCallId,
  targetConversation,
  patientSearchTerm,
  onPatientSearchTermChange,
  patientSearchLoading,
  patientSearchError,
  patientOptions,
  selectedPatientUuid,
  onSelectPatientUuid,
  selectedPatient,
  onClose,
  onAssign,
}) => {
  const targetIdLabel = targetCall
    ? `Call #${targetCall.call_uuid ?? targetCallId ?? "-"}`
    : targetConversation
      ? `Conversation #${targetConversation.conversationUuid}`
      : "Assignment Target";
  const canAssign = !!selectedPatient && !!(targetCall || targetConversation);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.assignModalOverlay} onPress={onClose}>
        <Pressable style={styles.assignModalCard} onPress={() => null}>
          <Text style={styles.cardTitle}>Assign to Patient</Text>
          <Text style={styles.metaText}>{targetIdLabel}</Text>
          {targetCall ? (
            <Text style={styles.metaText}>
              To:{" "}
              <PhoneNumberText
                value={targetCall.to_e164}
                style={styles.metaText}
              />
            </Text>
          ) : targetConversation ? (
            <Text style={styles.metaText}>
              Participant:{" "}
              <PhoneNumberText
                value={targetConversation.participantE164}
                style={styles.metaText}
              />
            </Text>
          ) : null}
          <Text style={styles.inputLabel}>Patient Search</Text>
          <SearchInput
            value={patientSearchTerm}
            onChangeText={onPatientSearchTermChange}
            onClear={() => onPatientSearchTermChange("")}
            loading={patientSearchLoading}
            placeholder="Search patients by name, email, or phone..."
            placeholderTextColor={ADMIN_TOKENS.color.textMuted}
            containerStyle={styles.patientSearchInputContainer}
            inputStyle={styles.patientSearchInput}
            iconColor={ADMIN_TOKENS.color.textMuted}
          />

          {patientSearchError ? (
            <Text style={styles.inlineErrorText}>{patientSearchError}</Text>
          ) : null}

          <ScrollView
            style={styles.assignModalPatientList}
            showsVerticalScrollIndicator={false}
          >
            {!patientOptions.length ? (
              <Text style={styles.emptyText}>
                {patientSearchTerm.trim()
                  ? "No matching patients found."
                  : "Start typing to search for a patient."}
              </Text>
            ) : (
              <View style={styles.listContainer}>
                {patientOptions.map((patient) => {
                  const isSelected = selectedPatientUuid === patient.uuid;
                  return (
                    <Pressable
                      key={patient.uuid}
                      style={[
                        styles.patientSearchResultRow,
                        isSelected && styles.patientSearchResultRowSelected,
                      ]}
                      onPress={() => onSelectPatientUuid(patient.uuid)}
                      accessibilityLabel={`Select patient ${patient.displayName}`}
                    >
                      <Text style={styles.callNumberText}>
                        {patient.displayName}
                      </Text>
                      <Text style={styles.metaText}>
                        {patient.email || `UUID: ${patient.uuid}`}
                      </Text>
                      <Text style={styles.metaText}>
                        {normalizePatientPhoneForCall(patient.phone)
                          ? `Phone: ${formatPhoneNumberDisplay(normalizePatientPhoneForCall(patient.phone))}`
                          : "No phone on file"}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            )}
          </ScrollView>

          {selectedPatient ? (
            <View style={styles.selectedPatientPanel}>
              <Text style={styles.callNumberText}>
                Selected: {selectedPatient.displayName}
              </Text>
              <Text style={styles.metaText}>UUID: {selectedPatient.uuid}</Text>
            </View>
          ) : null}

          <View style={styles.assignModalActionsRow}>
            <Pressable
              style={[
                styles.assignModalSecondaryButton,
                submitting && styles.callAgainButtonDisabled,
              ]}
              onPress={onClose}
              disabled={submitting}
              accessibilityLabel="Cancel patient assignment"
            >
              <Text style={styles.assignModalSecondaryButtonText}>Cancel</Text>
            </Pressable>
            <PrimaryButton
              text="Assign to Patient"
              onPress={onAssign}
              loading={submitting}
              disabled={!canAssign}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export default AssignPatientModal;
